import React, { useEffect, useMemo, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { v4 as uuidv4 } from "uuid";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn, currencyFormatter } from "@/lib/utils";
import { BudgetCategory, BudgetSubCategory, Currency } from "@/lib/types";
import { useGetBudgetSubCategoryById } from "@/services/queries/budgets";
import { InvoiceFormValues } from ".";
import { AutoComplete, Group, Option } from "@/components/ui/autocomplete";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const formSchema = z.object({
  id: z.string().optional(),
  description: z.string({ required_error: "Description is required" }).min(1),
  budgetSubcategoryId: z
    .string({ required_error: "Category is required" })
    .min(1, { message: "Category is required" }),
  quantity: z.number({ required_error: "Quantity is required" }),
  unitPrice: z.number({ required_error: "Unit price is required" }),
  vatRate: z.number({ required_error: "Tax rate is required" }),
});

const LineItemsForm = ({
  currencies,
  invoiceForm,
  categories,
}: {
  currencies: Currency[];
  invoiceForm: UseFormReturn<InvoiceFormValues>;
  categories: (BudgetCategory & { subCategories?: BudgetSubCategory[] })[];
}) => {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: undefined,
      description: "",
      budgetSubcategoryId: undefined,
      quantity: undefined,
      unitPrice: undefined,
      vatRate: undefined,
    },
  });

  const addNewLineItem = () => {
    invoiceForm.resetField("lineItems");
    setIsFormOpen(true);
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    const netValue = data.quantity * data.unitPrice;
    const vatValue = netValue * (data.vatRate / 100);
    const total = netValue + vatValue;

    invoiceForm.setValue("lineItems", [
      ...invoiceForm.watch("lineItems"),
      {
        ...data,
        id: uuidv4(),
        amount: total,
        vatAmount: vatValue,
      },
    ]);
    setIsFormOpen(false);
    form.reset();
  };

  const formatedCategories: Group[] = useMemo(() => {
    return categories.map((category) => ({
      groupName: category.name as string,
      options: category.subCategories?.map((subCategory) => ({
        value: subCategory.id as string,
        label: subCategory.name as string,
      })) as Option[],
    }));
  }, [categories]);

  return (
    <div className="!mt-5">
      <div className="mb-6 flex items-center justify-between">
        <span className="text-[15px] font-medium leading-5 text-[#12A0B9]">
          {t("title.invoices")}
        </span>

        <Button
          data-testid="invoice-form-line-item-add-button"
          variant="default"
          size="sm"
          onClick={addNewLineItem}
        >
          {t("button.add_new_record")}
        </Button>
      </div>

      {invoiceForm.watch("lineItems").length <= 0 && !isFormOpen && (
        <div className="flex h-[95px] w-full items-center justify-center rounded-lg border border-white/20 px-8 text-base text-white/50">
          {t("error_message.no_line_items")}
        </div>
      )}

      {isFormOpen && (
        <NewLineItemForm
          form={form}
          onSubmit={onSubmit}
          setIsFormOpen={setIsFormOpen}
          budgetId={invoiceForm.watch("budgetId") ?? ""}
          categories={formatedCategories}
        />
      )}

      <div className="space-y-4">
        {invoiceForm.watch("lineItems").map((lineItem) => {
          return (
            <LineItem
              key={lineItem.id}
              lineItem={lineItem}
              currency={currencies.find(
                (currency) => currency.id === invoiceForm.watch("currencyId")
              )}
              invoiceForm={invoiceForm}
              categories={formatedCategories}
              budgetId={invoiceForm.watch("budgetId") ?? ""}
            />
          );
        })}
      </div>
    </div>
  );
};

const NewLineItemForm = ({
  form,
  onSubmit,
  setIsFormOpen,
  budgetId,
  categories,
  defaultValues,
  editMode,
}: {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  setIsFormOpen: (isFormOpen: boolean) => void;
  budgetId?: string;
  categories: Group[];
  defaultValues?: z.infer<typeof formSchema>;
  editMode?: boolean;
}) => {
  const { t } = useTranslation();

  const calculatedValues = useMemo(() => {
    const netValue = Number(
      ((form.watch("unitPrice") ?? 0) * (form.watch("quantity") ?? 0)).toFixed(
        2
      )
    );
    const taxValue = Number(
      ((netValue * (form.watch("vatRate") ?? 0)) / 100).toFixed(2)
    );

    const lineTotal = Number((netValue + taxValue).toFixed(2));

    return { netValue, taxValue, lineTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch("unitPrice"), form.watch("quantity"), form.watch("vatRate")]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.warn(errors);
        })}
        id="add-new-line-item-form"
        className="w-full space-y-[15px] rounded-lg border border-white/20 bg-[#1FB3CC0D] px-[15px] py-[17px]"
      >
        <FormField
          control={form.control}
          name={`description`}
          render={({ field }) => (
            <FormItem className="gap-2">
              <FormLabel className="font-normal">
                {t("label.description")}
              </FormLabel>
              <FormControl>
                <Input
                  data-testid={
                    defaultValues?.id
                      ? `invoice-form-line-item-${defaultValues?.id}-description-input`
                      : "invoice-form-new-line-item-description-input"
                  }
                  severity="light"
                  className="autofill-text-color rounded-lg"
                  size="sm"
                  invalid={!!form.formState.errors.description}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`budgetSubcategoryId`}
          render={({ field }) => {
            let label = "";

            if (field.value) {
              for (const category of categories) {
                const option = category.options.find(
                  (option) => option.value === field.value
                );

                if (option) {
                  label = option.label;
                  break;
                }
              }
            }

            return (
              <FormItem className="gap-2">
                <FormLabel className="font-normal">
                  {t("label.category")}
                </FormLabel>
                <FormControl>
                  <AutoComplete
                    data-testid={
                      defaultValues?.id
                        ? `invoice-form-line-item-${defaultValues?.id}-category-autocomplete`
                        : "invoice-form-new-line-item-category-autocomplete"
                    }
                    invalid={!!form.formState.errors.budgetSubcategoryId}
                    options={categories}
                    value={{
                      label,
                      value: field.value,
                    }}
                    allowEmptySelection={false}
                    onValueChange={(e) => {
                      if (e) {
                        field.onChange(e.value);
                      } else {
                        field.onChange("");
                      }
                    }}
                    emptyMessage={
                      budgetId === ""
                        ? t("description.select_budget_before_category")
                        : t("description.not_found")
                    }
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <div className="flex w-full flex-row gap-[15px]">
          <FormField
            control={form.control}
            name={`quantity`}
            render={({ field }) => (
              <FormItem className="w-full gap-2">
                <FormLabel className="font-normal">
                  {t("label.quantity")}
                </FormLabel>
                <FormControl>
                  <Input
                    data-testid={
                      defaultValues?.id
                        ? `invoice-form-line-item-${defaultValues?.id}-quantity-input`
                        : "invoice-form-new-line-item-quantity-input"
                    }
                    severity="light"
                    className="rounded-lg"
                    size="sm"
                    type="number"
                    invalid={!!form.formState.errors.quantity}
                    {...field}
                    value={field.value || field.value === 0 ? field.value : ""}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value === "" ? "" : Number(e.target.value)
                      );
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`unitPrice`}
            render={({ field }) => (
              <FormItem className="w-full gap-2">
                <FormLabel className="font-normal">
                  {t("label.unit_price")}
                </FormLabel>
                <FormControl>
                  <Input
                    data-testid={
                      defaultValues?.id
                        ? `invoice-form-line-item-${defaultValues?.id}-unit-price-input`
                        : "invoice-form-new-line-item-unit-price-input"
                    }
                    severity="light"
                    className="rounded-lg"
                    size="sm"
                    type="number"
                    invalid={!!form.formState.errors.unitPrice}
                    {...field}
                    value={field.value || field.value === 0 ? field.value : ""}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value === "" ? "" : Number(e.target.value)
                      );
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex w-full flex-row items-start gap-[15px]">
          <div className="w-full space-y-1">
            <FormLabel className="font-normal">
              {t("label.net_value")}
            </FormLabel>
            <div className="flex h-8 w-full cursor-not-allowed items-center rounded-lg bg-white px-3 text-left text-sm text-neutral-900">
              {calculatedValues.netValue}
            </div>
          </div>

          <FormField
            control={form.control}
            name={`vatRate`}
            render={({ field }) => (
              <FormItem className="w-full gap-2">
                <FormLabel className="font-normal">
                  {t("label.tax")} %
                </FormLabel>
                <FormControl>
                  <Input
                    data-testid={
                      defaultValues?.id
                        ? `invoice-form-line-item-${defaultValues?.id}-vat-rate-input`
                        : "invoice-form-new-line-item-vat-rate-input"
                    }
                    severity="light"
                    className="w-full rounded-lg"
                    size="sm"
                    type="number"
                    invalid={!!form.formState.errors.vatRate}
                    max={100}
                    {...field}
                    value={field.value || field.value === 0 ? field.value : ""}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value === "" ? "" : Number(e.target.value)
                      );
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex w-full flex-row items-start gap-[15px]">
          <div className="w-full space-y-1">
            <FormLabel className="font-normal">
              {t("label.tax_value")}
            </FormLabel>
            <div className="flex h-8 w-full cursor-not-allowed items-center rounded-lg bg-white px-3 text-left text-sm text-neutral-900">
              {calculatedValues.taxValue}
            </div>
          </div>

          <div className="w-full space-y-1">
            <FormLabel className="font-normal">
              {t("label.line_total")}
            </FormLabel>
            <div className="flex h-8 w-full cursor-not-allowed items-center rounded-lg bg-white px-3 text-left text-sm text-neutral-900">
              {calculatedValues.lineTotal}
            </div>
          </div>
        </div>

        <div className="!mt-6 flex w-full items-center justify-end gap-[15px]">
          <Button
            onClick={() => {
              if (editMode) {
                form.reset(defaultValues);
                setIsFormOpen(false);
              } else {
                setIsFormOpen(false);
              }
            }}
            variant="dark"
            className="w-2/5"
            size="sm"
            type="button"
            data-testid={
              defaultValues?.id
                ? `invoice-form-line-item-${defaultValues?.id}-cancel-button`
                : "invoice-form-new-line-item-cancel-button"
            }
          >
            {t("button.cancel")}
          </Button>
          <Button
            className="w-3/5"
            size="sm"
            form="add-new-line-item-form"
            type="submit"
            data-testid={
              defaultValues?.id
                ? `invoice-form-line-item-${defaultValues?.id}-save-button`
                : "invoice-form-new-line-item-save-button"
            }
          >
            {t("button.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

const LineItem = ({
  lineItem,
  currency,
  invoiceForm,
  categories,
  budgetId,
}: {
  lineItem: InvoiceFormValues["lineItems"][number];
  currency?: Currency;
  invoiceForm: UseFormReturn<InvoiceFormValues>;
  categories: Group[];
  budgetId: string;
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: lineItem.id ?? "",
      description: lineItem.description,
      budgetSubcategoryId: lineItem.budgetSubcategoryId ?? "",
      quantity: lineItem.quantity,
      unitPrice: lineItem.unitPrice,
      vatRate: lineItem.vatRate,
    },
  });

  useEffect(() => {
    form.reset({
      id: lineItem.id ?? "",
      description: lineItem.description,
      budgetSubcategoryId: lineItem.budgetSubcategoryId ?? "",
      quantity: lineItem.quantity,
      unitPrice: lineItem.unitPrice,
      vatRate: lineItem.vatRate,
    });
  }, [lineItem, form]);

  const { data: subCategoryData } = useGetBudgetSubCategoryById({
    id: form.watch("budgetSubcategoryId") ?? "",
  });

  const subCategory = useMemo(() => {
    return subCategoryData?.value;
  }, [subCategoryData]);

  const calculatedValues = useMemo(() => {
    const netValue = (lineItem.unitPrice ?? 0) * (lineItem.quantity ?? 0);
    const taxValue = (netValue * (lineItem.vatRate ?? 0)) / 100;
    const lineTotal = netValue + taxValue;

    return { netValue, taxValue, lineTotal };
  }, [lineItem.unitPrice, lineItem.quantity, lineItem.vatRate]);

  const details = useMemo(() => {
    return [
      {
        label: t("label.category"),
        value: subCategory ? subCategory?.name : "-",
      },
      {
        label: t("label.unit_price"),
        value: currencyFormatter(lineItem.unitPrice ?? 0, currency?.code),
      },
      {
        label: t("label.tax"),
        value: currencyFormatter(
          calculatedValues.taxValue ?? 0,
          currency?.code
        ),
      },
      {
        label: t("label.total"),
        value: currencyFormatter(calculatedValues.lineTotal, currency?.code),
      },
    ];
  }, [
    t,
    subCategory,
    lineItem.unitPrice,
    currency?.code,
    calculatedValues.taxValue,
    calculatedValues.lineTotal,
  ]);

  const handleDelete = (id: string) => {
    const lineItems = invoiceForm.getValues("lineItems");

    invoiceForm.setValue(
      "lineItems",
      lineItems.filter((lineItem) => lineItem.id !== id)
    );
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    const { vatRate, unitPrice, quantity } = data;
    const netValue = unitPrice * quantity;
    const vatValue = (netValue * vatRate) / 100;
    const total = netValue + vatValue;

    invoiceForm.setValue(
      "lineItems",
      invoiceForm.getValues("lineItems").map((lineItem) =>
        lineItem.id === data.id
          ? {
              ...lineItem,
              vatAmount: vatValue,
              amount: total,
              ...data,
            }
          : lineItem
      )
    );
    setIsEditing(false);
  };

  const emptyCategory =
    form.watch("budgetSubcategoryId") === "" ||
    form.watch("budgetSubcategoryId") === undefined;

  useEffect(() => {
    if (emptyCategory) {
      setIsExpanded(true);
    }
  }, [emptyCategory, form, t]);

  if (isEditing) {
    return (
      <NewLineItemForm
        form={form}
        onSubmit={onSubmit}
        setIsFormOpen={() => setIsEditing(false)}
        budgetId={invoiceForm.watch("budgetId") ?? ""}
        categories={categories}
        editMode={true}
        defaultValues={{
          id: lineItem.id ?? "",
          description: lineItem.description,
          budgetSubcategoryId: lineItem.budgetSubcategoryId ?? "",
          quantity: lineItem.quantity,
          unitPrice: lineItem.unitPrice,
          vatRate: lineItem.vatRate,
        }}
      />
    );
  }

  const renderSubCategoryEditing = () => {
    let label = "";

    if (lineItem.budgetSubcategoryId) {
      for (const category of categories) {
        const option = category.options.find(
          (option) => option.value === lineItem.budgetSubcategoryId
        );

        if (option) {
          label = option.label;
          break;
        }
      }
    }

    return (
      <AutoComplete
        data-testid={`invoice-form-line-item-${lineItem.id}-category-autocomplete`}
        className="h-8 w-full max-w-sm"
        invalid={!!form.formState.errors.budgetSubcategoryId}
        options={categories}
        value={{ label, value: lineItem.budgetSubcategoryId ?? "" }}
        onValueChange={(e) => {
          if (e) {
            form.setValue("budgetSubcategoryId", e.value);
            invoiceForm.setValue(
              "lineItems",
              invoiceForm
                .getValues("lineItems")
                .map((li) =>
                  li.id === lineItem.id
                    ? { ...li, budgetSubcategoryId: e.value }
                    : li
                )
            );
          } else {
            form.setValue("budgetSubcategoryId", "");
          }
        }}
        emptyMessage={
          budgetId === ""
            ? t("description.select_budget_before_category")
            : t("description.not_found")
        }
      />
    );
  };

  return (
    <div
      className="space-y-1"
      data-testid={`invoice-form-line-item-${
        lineItem.id ?? lineItem.description.toLowerCase().replace(/ /g, "-")
      }`}
    >
      <div
        className={cn(
          "w-full space-y-[15px] rounded-lg border border-white/20 bg-[#1FB3CC0D]"
        )}
      >
        <button
          onClick={() => {
            if (emptyCategory) return;

            setIsExpanded(!isExpanded);
          }}
          className="flex w-full flex-col justify-start gap-1 px-[15px] py-3"
          data-testid={`invoice-form-line-item-${lineItem.id}-expand-button`}
        >
          <div className="flex w-full items-center justify-between">
            <span className="text-left text-sm text-white">
              {lineItem.description}
            </span>
            <span className="text-white/60">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </span>
          </div>
          {isExpanded ? (
            <span className="text-left text-sm font-medium text-white/60">
              {lineItem.quantity} {t("label.quantity")}
            </span>
          ) : (
            <span className="text-left text-sm font-medium text-[#7ECEE8]">
              {currencyFormatter(calculatedValues.lineTotal, currency?.code)}
            </span>
          )}
        </button>

        {isExpanded && (
          <div className="px-[15px] pb-3">
            <div className="h-px w-full bg-white/10" />

            {details.map((detail) => (
              <React.Fragment key={uuidv4()}>
                <div
                  className={cn(
                    "flex items-center justify-between gap-3 py-2.5",
                    {
                      "h-10 py-0":
                        emptyCategory && detail.label === t("label.category"),
                    }
                  )}
                >
                  <span className="text-sm text-white">{detail.label}</span>
                  {emptyCategory && detail.label === t("label.category") ? (
                    renderSubCategoryEditing()
                  ) : (
                    <span className="text-sm font-medium text-[#7ECEE8]">
                      {detail.value}
                    </span>
                  )}
                </div>
                <div className="h-px w-full bg-white/10" />
              </React.Fragment>
            ))}

            {canWriteExpenses && (
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  onClick={() => setIsEditing(true)}
                  size="sm"
                  data-testid={`invoice-form-line-item-${lineItem.id}-edit-button`}
                >
                  {t("button.edit")}
                </Button>

                <Button
                  onClick={() => handleDelete(lineItem.id as string)}
                  size="sm"
                  data-testid={`invoice-form-line-item-${lineItem.id}-delete-button`}
                >
                  {t("button.delete")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {(form.watch("budgetSubcategoryId") === "" ||
        form.watch("budgetSubcategoryId") === undefined ||
        form.watch("quantity") === undefined ||
        form.watch("unitPrice") === undefined ||
        form.watch("vatRate") === undefined) && (
        <span
          data-testid={`invoice-form-line-${lineItem.id}-missing-error`}
          className="flex items-center gap-1 text-[13px] font-medium leading-[17px] text-[#E86363]"
        >
          {t("error_message.missing_invoice_information")}
        </span>
      )}
    </div>
  );
};

export default LineItemsForm;
